import * as React from 'react';
import {useEffect, useMemo} from 'react';
import {DateInput} from 'components/form/DateInput';
import {OptionType, Select} from 'components/form/Select';
import {TextInput} from 'components/form/TextInput';
import {TextArea} from 'components/form/TextArea';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {ColorCodeInput} from 'components/form/ColorCodeInput';
import {FormShell} from 'components/form/FormShell';
import {usePrompt} from 'helpers/useBlocker';
import {observer} from 'mobx-react-lite';
import {appState} from 'store/AppStore';
import {VehicleState} from 'store/vehicle/VehicleState';
import {CarMainDto} from '../../../../../../PRMGApiClient';
import {VehicleEditKey} from '../VehicleEditView';

interface VehicleKeySpecificationsFormProps {
  state: VehicleState;
}

export const VehicleKeySpecificationsForm: React.FC<VehicleKeySpecificationsFormProps> = observer(
  ({state}): JSX.Element => {
    const {garageId, vehicleId} = useParams();
    const {t} = useTranslation();
    const {
      register,
      control,
      handleSubmit,
      watch,
      reset,
      setValue,
      formState: {errors, isDirty},
    } = useForm<CarMainDto>({
      mode: 'onTouched',
      defaultValues: {
        garageId: Number(garageId ?? '0'),
        plateNumber: '',
        vin: '',
        remarks: '',
        location: '',
        calendarColorRGB: '',
        currentMileage: null,
        currentWorth: null,
      },
    });

    const {commonData} = appState;

    const mark = watch('carMakeId');

    /* Model opitons change depending on selected vehicle brand */
    const modelOptions = useMemo(() => {
      if (commonData?.carMakesWithModels && mark) {
        const markData = commonData.carMakesWithModels.find((item) => item.id === mark);
        return markData?.models?.map((model) => ({
          value: model.carModelId,
          label: model.modelName,
        }));
      }
    }, [commonData?.carMakesWithModels, mark]);

    useEffect(() => {
      if (vehicleId) reset({...state.item.main, garageId: Number(garageId)});
    }, [state.item, vehicleId, reset, garageId]);

    useEffect(() => {
      state.setDirty(VehicleEditKey.keySpecifications, isDirty);
    }, [isDirty]);

    usePrompt(t('Are you sure? There are unsaved changes.'), vehicleId ? isDirty : false);

    return (
      <FormShell
        isDirty={isDirty}
        onReset={() => reset()}
        onSubmit={handleSubmit((payload) => state.save(payload, false))}
        onSubmitAndExit={vehicleId ? undefined : handleSubmit((payload) => state.save(payload, true))}
        isLoading={state.isLoading}
      >
        <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2 lg:grid-cols-2">
          <Controller
            name="carMakeId"
            control={control}
            rules={{required: true}}
            render={({field: {onChange, value}}) => (
              <Select
                id="carMakeId"
                label={t('Brand')}
                options={appState.markOptions}
                value={appState.markOptions?.find((item) => item.value === value) ?? null}
                required={true}
                error={errors.carMakeId}
                onChange={(e) => {
                  onChange((e as OptionType).value);
                  setValue('carModelId', undefined);
                }}
              />
            )}
          />
          <Controller
            name="carModelId"
            control={control}
            rules={{required: true}}
            render={({field: {onChange, value}}) => (
              <Select
                id="carModelId"
                label={t('Model')}
                options={modelOptions}
                value={modelOptions?.find((item) => item.value === value) ?? null}
                required={true}
                error={errors.carModelId}
                onChange={(e) => onChange((e as OptionType).value)}
              />
            )}
          />
          <TextInput
            label={t('Plate No')}
            {...register('plateNumber', {required: true})}
            required={true}
            error={errors.plateNumber}
          />
          <TextInput
            label={t('VIN')}
            {...register('vin', {
              required: true,
              validate: (value) =>
                /^[A-HJ-NPR-Z0-9]{17}$/.test(String(value)) ||
                String(t('Wrong VIN format (17 characters, the latters must be capital, I, O, Q are not allowed)')),
            })}
            required={true}
            error={errors.vin}
          />
          <Controller
            name="manufactureYear"
            control={control}
            render={({field: {onChange, value}}) => (
              <Select
                id="manufactureYear"
                label={t('Production year')}
                options={appState.manufactureYearOptions}
                value={appState.manufactureYearOptions?.find((item) => item.value === value) ?? null}
                error={errors.manufactureYear}
                onChange={(e) => onChange((e as OptionType).value)}
              />
            )}
          />
          <Controller
            name="firstRegistration"
            control={control}
            render={({field: {onChange, value}}) => (
              <DateInput
                id="firstRegistration"
                label={t('First registration date')}
                value={value}
                error={errors.firstRegistration}
                onChange={(e) => onChange(e)}
              />
            )}
          />
          <TextInput
            label={t('Car milage (km)')}
            type="number"
            {...register('currentMileage', {required: true})}
            error={errors.currentMileage}
          />
          <TextInput
            label={t('Car worth ($)')}
            type="number"
            {...register('currentWorth')}
            error={errors.currentWorth}
          />
          <Controller
            name="calendarColorRGB"
            control={control}
            render={({field: {onChange, value}}) => (
              <ColorCodeInput
                id="calendarColorRGB"
                label={t('Color')}
                value={value as string}
                onChange={(color) => onChange(color)}
                error={errors.calendarColorRGB}
              />
            )}
          />
          <Controller
            name="status"
            control={control}
            rules={{required: true}}
            render={({field: {onChange, value}}) => (
              <Select
                id="status"
                label={t('Status')}
                options={appState.statusOptions}
                value={appState.statusOptions?.find((item) => item.value === value) ?? null}
                required={true}
                error={errors.status}
                onChange={(e) => onChange((e as OptionType).value)}
              />
            )}
          />
          <TextArea label={t('Description')} {...register('remarks')} error={errors.remarks} />
          <TextInput label={t('Location')} {...register('location')} error={errors.location} />
        </div>
      </FormShell>
    );
  }
);
